import React, { Suspense, lazy, useMemo, useState, useEffect } from 'react';
import { useTenant } from '../../hooks/useTenant';

type TemplateKey = 'home' | 'contact' | 'about';

interface TemplateRouteProps {
  templateKey: TemplateKey;
  defaultElement: React.ReactElement;
}

// Mapping dynamique des templates disponibles (Vite)
const templateModules = import.meta.glob('/src/templates/**/*.{tsx,jsx,ts,js}');

export const TemplateRoute: React.FC<TemplateRouteProps> = ({ templateKey, defaultElement }) => {
  const { tenant, loading } = useTenant();
  const selectedPath = (tenant?.config?.templates?.[templateKey] as string | undefined) || '';
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [TemplateComponent, setTemplateComponent] = useState<React.ComponentType | null>(null);

  // Charger le template dynamiquement sans Suspense
  useEffect(() => {
    let isMounted = true;
    if (selectedPath && templateModules[selectedPath]) {
      setLoadingTemplate(true);
      const loadModule = templateModules[selectedPath] as () => Promise<{ default: React.ComponentType }>;
      loadModule().then(module => {
        if (isMounted) {
          setTemplateComponent(() => module.default);
          setLoadingTemplate(false);
        }
      }).catch(err => {
        console.error('Erreur de chargement du template:', err);
        if (isMounted) {
          setLoadingTemplate(false);
        }
      });
    } else {
      setTemplateComponent(null);
      setLoadingTemplate(false);
    }
    return () => { isMounted = false; };
  }, [selectedPath]);

  if (loading || loadingTemplate) {
    // Pendant le chargement des infos tenant ou du template, ne rien afficher
    return null;
  }

  if (TemplateComponent) {
    return <TemplateComponent />;
  }

  // Aucun template spécifique sélectionné -> page par défaut
  return defaultElement;
};
